import React from 'react';
import RLinkLogo from './RLinkLogo';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleRejoin = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-[#001233] to-[#6a1fbf] flex items-center justify-center p-4">
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 max-w-md w-full mx-4 text-center">
            <div className="mb-6 flex justify-center">
              <RLinkLogo size="lg" />
            </div>

            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>

            <h1 className="text-2xl font-bold text-white mb-2">
              Something went wrong
            </h1>
            <p className="text-white/70 text-sm mb-4">
              The meeting experienced an unexpected error. You can try to rejoin or reload the page.
            </p>

            {/* Show actual error details to help debug */}
            {this.state.error && (
              <details className="mb-6 text-left bg-black/30 rounded-lg p-3 text-xs text-white/70 max-h-60 overflow-auto">
                <summary className="cursor-pointer text-white/90 font-medium mb-2">Error details</summary>
                <div className="font-mono whitespace-pre-wrap break-words">
                  <div className="text-red-300 mb-2">
                    {this.state.error.toString()}
                  </div>
                  {this.state.error.stack && (
                    <div className="text-white/50 text-[10px]">
                      {this.state.error.stack}
                    </div>
                  )}
                  {this.state.errorInfo?.componentStack && (
                    <div className="text-blue-300 text-[10px] mt-2">
                      Component stack:
                      {this.state.errorInfo.componentStack}
                    </div>
                  )}
                </div>
              </details>
            )}

            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleRejoin}
                className="px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-xl hover:opacity-90 transition-opacity"
              >
                Try Again
              </button>
              <button
                onClick={this.handleReload}
                className="px-5 py-2.5 text-sm font-medium text-white/80 bg-white/10 hover:bg-white/20 rounded-xl transition-colors border border-white/10"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
